import { type ReactNode } from 'react';
import { ActionIcon, Group, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconCopy, IconTrash } from '@tabler/icons-react';

export interface OrderedEntry<T> {
    key: string;
    value: T;
}

interface OrderedEntryListProps<T> {
    entries: OrderedEntry<T>[];
    onChange: (entries: OrderedEntry<T>[]) => void;
    renderEntry: (value: T, index: number) => ReactNode;
    emptyMessage?: string;
    disabled?: boolean;
}

const moveEntry = <T,>(entries: OrderedEntry<T>[], from: number, to: number): OrderedEntry<T>[] => {
    const next = [...entries];
    const [moved] = next.splice(from, 1);

    next.splice(to, 0, moved);

    return next;
};

export const OrderedEntryList = <T,>({
    entries,
    onChange,
    renderEntry,
    emptyMessage = 'Список пуст',
    disabled = false,
}: OrderedEntryListProps<T>) => {
    const handleMoveUp = (index: number) => {
        if (index === 0) {
            return;
        }

        onChange(moveEntry(entries, index, index - 1));
    };

    const handleMoveDown = (index: number) => {
        if (index === entries.length - 1) {
            return;
        }

        onChange(moveEntry(entries, index, index + 1));
    };

    const handleDuplicate = (index: number) => {
        const entry = entries[index];
        const next = [...entries];

        next.splice(index + 1, 0, { ...entry, key: `${entry.key}-copy-${Date.now()}-${Math.random()}` });
        onChange(next);
    };

    const handleRemove = (index: number) => {
        onChange(entries.filter((_, entryIndex) => entryIndex !== index));
    };

    if (entries.length === 0) {
        return (
            <Text
                size='sm'
                c='dimmed'
            >
                {emptyMessage}
            </Text>
        );
    }

    return (
        <Stack gap='xs'>
            {entries.map((entry, index) => (
                <Paper
                    key={entry.key}
                    withBorder
                    radius='sm'
                    p='xs'
                >
                    <Group
                        justify='space-between'
                        wrap='nowrap'
                        gap='sm'
                    >
                        <Group
                            gap='sm'
                            wrap='nowrap'
                            flex={1}
                            miw={0}
                        >
                            <Text
                                size='sm'
                                c='dimmed'
                                w={24}
                            >
                                {index + 1}
                            </Text>

                            {renderEntry(entry.value, index)}
                        </Group>

                        <Group
                            gap={4}
                            wrap='nowrap'
                        >
                            <Tooltip label='Переместить вверх'>
                                <ActionIcon
                                    variant='subtle'
                                    size='sm'
                                    disabled={disabled || index === 0}
                                    onClick={() => handleMoveUp(index)}
                                >
                                    <IconArrowUp size={16} />
                                </ActionIcon>
                            </Tooltip>

                            <Tooltip label='Переместить вниз'>
                                <ActionIcon
                                    variant='subtle'
                                    size='sm'
                                    disabled={disabled || index === entries.length - 1}
                                    onClick={() => handleMoveDown(index)}
                                >
                                    <IconArrowDown size={16} />
                                </ActionIcon>
                            </Tooltip>

                            <Tooltip label='Дублировать'>
                                <ActionIcon
                                    variant='subtle'
                                    size='sm'
                                    disabled={disabled}
                                    onClick={() => handleDuplicate(index)}
                                >
                                    <IconCopy size={16} />
                                </ActionIcon>
                            </Tooltip>

                            <Tooltip label='Удалить'>
                                <ActionIcon
                                    variant='subtle'
                                    color='red'
                                    size='sm'
                                    disabled={disabled}
                                    onClick={() => handleRemove(index)}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    </Group>
                </Paper>
            ))}
        </Stack>
    );
};
